import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { BackLink } from "@/components/explore/back-link";
import { EpisodeCard } from "@/components/explore/episode-card";
import { TagList } from "@/components/explore/tag-list";
import { Image } from "@/components/image";
import { dummyIcon } from "@/lib/consts";
import { episodesInfiniteQuery, seedEpisodes, showQuery } from "@/lib/queries";

export function ShowPage({ showId }: { showId: string }) {
  const queryClient = useQueryClient();
  const sentinel = useRef<HTMLDivElement>(null);

  // Both are usually already cached — the show from the shows list, the
  // episodes seeded by whichever list the visitor came from — so this
  // renders immediately and revalidates behind the scenes.
  const { data: show } = useQuery(showQuery(showId));
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(episodesInfiniteQuery(showId));

  const episodes = useMemo(
    () => data?.pages.flatMap((page) => page.episodes) ?? [],
    [data]
  );

  useEffect(() => {
    seedEpisodes(queryClient, episodes);
  }, [episodes, queryClient]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage]);

  return (
    <div>
      <BackLink className="mb-6" />

      {show ? (
        <header className="flex items-center gap-6">
          <div className="relative aspect-square w-28 shrink-0 overflow-hidden rounded-2xl bg-muted shadow-sm sm:w-36">
            <Image
              fill
              src={show.iconUrl ?? dummyIcon}
              alt={`${show.name} art`}
              className="object-cover"
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              {show.name}
            </h1>
            {show.description && (
              <p className="mt-2 text-muted-foreground">{show.description}</p>
            )}
            <div className="mt-3">
              <TagList tags={show.tags} />
            </div>
          </div>
        </header>
      ) : (
        <p className="text-muted-foreground">Loading…</p>
      )}

      <h2 className="mt-10 text-xl font-semibold tracking-tight">Episodes</h2>

      <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
        {episodes.map((episode, index) => (
          <EpisodeCard
            episode={episode}
            key={episode.id}
            priority={index < 5}
          />
        ))}
      </div>

      {show && episodes.length === 0 && (
        <p className="mt-4 text-muted-foreground">No episodes yet.</p>
      )}

      <div aria-hidden className="h-px" ref={sentinel} />

      {isFetchingNextPage && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Loading more…
        </p>
      )}
    </div>
  );
}
